import ListPresenter from "@/utils/common/ListPresenter.ts";
import EntityViewCustomizer from "@/utils/common/EntityViewCustomizer.ts";
import {CriteriaExpressionSerializer} from "@/utils/urlUtils/CriteriaAndQuestionsURLParser.ts";
import CriteriaTreeUtils from "@/utils/common/CriteriaTreeUtils.ts";
import {GridApi} from "ag-grid-community";
import PlaceTokens from "@/utils/constants/PlaceTokens.ts";
import CurrentUserUtils from "@/utils/login/CurrentUserUtils.ts";
import {QuestionFieldsPresenter} from "@/utils/presenters/QuestionFieldsPresenter.ts";
import CacheUtil from "@/utils/login/CacheUtil.ts";
import {EditPresenter} from "@/utils/presenters/EditPresenter.ts";
import NamaCommon from "@/utils/common/NamaCommon.ts";
import CommonFieldIds from "@/utils/constants/CommonFieldIds.ts";
import FieldTypesUtils from "@/utils/common/FieldTypesUtils.ts";
import FieldType from "@/utils/constants/FieldType.ts";
import MetaData from "@/utils/common/MetaData.ts";
import EditScreenListLinkType from "@/utils/constants/EditScreenListLinkType.ts";
import {WidgetsPresenter} from "@/utils/presenters/interfaces.ts";
import QuestionField = namaTypes.QuestionField;
import EditScreenList = namaTypes.EditScreenList;
import DTOFilledQuestionField = namaTypes.DTOFilledQuestionField;
import EntityReferenceData = namaTypes.EntityReferenceData;

export default class EditViewListPresenter extends ListPresenter
{
  hiddenQuestionFields: string[] = [];
  hiddenCriteriaFields: string[] = [];
  questionFieldPresenter: QuestionFieldsPresenter;
  widgetPresenter: WidgetsPresenter;
  block: EditScreenList;

  constructor(editPresenter: WidgetsPresenter, block: EditScreenList) {
    let query = {
      [PlaceTokens.PRM_ENTITY_TYPE]: block.listViewEntityType!,
      [PlaceTokens.PRM_PAGE]: "0",
      [PlaceTokens.PRM_PAGE_SIZE]: CurrentUserUtils.calcDefaultPageSize(CurrentUserUtils.defaultPageSize()).toString(),
      [PlaceTokens.PRM_LIST_VIEW_NAME]: block.listViewId!,
    };
    super(query);
    this.questionFieldPresenter = new QuestionFieldsPresenter(block.listViewEntityType!);
    this.widgetPresenter = editPresenter;
    this.block = block;
    if (block.hideLinkField) {
      if (block.linkType === EditScreenListLinkType.QuestionField)
        this.hideQuestionField(block.linkFieldId);
      for (let hiddenQuestion of block.hiddenQuestions ?? [])
        this.hideQuestionField(hiddenQuestion);
    }
  }

  override async start() {
    this.hiddenQuestionFields.push(...EntityViewCustomizer.getHiddenQuestionsInListView(this.entityType!, this.fetchPlace().listViewName ?? ''));
    await super.start();
    let overriddenQuestions = this.block.overriddenQuestions;
    let cachedQuestionFields = CacheUtil.getCachedQuestionFields(this.entityType, this.fetchPlace().listViewName);
    this.availableQuestionFields.forEach(l => {
      l.metaData!['enabled'] = false;
      l.metaData!['guiField'] = true;
    });
    await this.questionFieldPresenter.edit(this.availableQuestionFields, overriddenQuestions ?? cachedQuestionFields, this.hiddenQuestionFields);
  }

  override initCriteriaAndQuestionFields() {
    super.initCriteriaAndQuestionFields();
    this.availableCriteriaFields = this.availableCriteriaFields.filter(l => !this.hiddenCriteriaFields.includes(l.id ?? ''));
  }

  override async silentlyUpdateCriteriaInURL(): Promise<void> {
    this.fetchPlace().criteria = new CriteriaExpressionSerializer().toURL(this.listViewCriteria);
  }

  override async removeFilter(api: GridApi) {
    this.fetchPlace().criteria = '';
    this.listViewCriteria = undefined;
    this.criteriaTree.value = CriteriaTreeUtils.criteriaToTree(undefined, this.availableCriteriaFields);
    this.selectedQuickFilterGroups.value = [];
    if (api && !api.isDestroyed()) {
      api.setFilterModel(null);
      api.refreshServerSide({purge: true});
    }
  }

  hideQuestionField(questionFieldId?: string) {
    if (!questionFieldId)
      return;
    if (questionFieldId.includes(",")) {
      let parts = questionFieldId.split(",");
      for (let q of parts) {
        this.hideQuestionField(q);
      }
    }
    if (!this.hiddenQuestionFields.contains(questionFieldId))
      this.hiddenQuestionFields.push(questionFieldId);
  }

  override fetchFilledQuestionFields(source: QuestionField[]) {
    let questionFields = this.questionFieldPresenter.getQuestionFields();
    let linkFields: DTOFilledQuestionField[] = this.findLinkedFields(this.block.linkFieldId, questionFields);
    let linkFieldsId = linkFields.map(x => x.id);
    questionFields = questionFields.filter(l => !linkFieldsId.contains(l.id));
    let newFieldQuestions = this.createNewFieldQuestionIds(this.block.linkFieldId, source, linkFields);
    linkFields.push(...newFieldQuestions);
    linkFieldsId = linkFields.map(x => x.id);
    if (this.widgetPresenter instanceof EditPresenter) {
      if (this.block.sourceField) {
        this.readSourceFields(questionFields, this.block.sourceField, this.block.linkFieldId);
      } else {
        for (let fieldId of linkFieldsId) {
          if (!fieldId)
            continue;
          if (fieldId === this.block.linkFieldId) {
            let value: EntityReferenceData = {
              entityType: NamaCommon.getFieldValue(this.widgetPresenter.fetchRecord(), CommonFieldIds.ENTITY_TYPE),
              id: NamaCommon.getFieldValue(this.widgetPresenter.fetchRecord(), CommonFieldIds.ID),
              code: NamaCommon.getFieldValue(this.widgetPresenter.fetchRecord(), CommonFieldIds.CODE),
              actualCode: NamaCommon.getFieldValue(this.widgetPresenter.fetchRecord(), CommonFieldIds.ACTUAL_CODE),
              altCode: NamaCommon.getFieldValue(this.widgetPresenter.fetchRecord(), CommonFieldIds.ALT_CODE),
              name1: NamaCommon.getFieldValue(this.widgetPresenter.fetchRecord(), CommonFieldIds.NAME1),
              name2: NamaCommon.getFieldValue(this.widgetPresenter.fetchRecord(), CommonFieldIds.NAME2),
            };
            questionFields.push({
              id: fieldId,
              objValue: value,
              value: FieldTypesUtils.toString(FieldType.Reference, value)
            });
          } else {
            let fieldMetadata = MetaData.findFieldMetadata(fieldId, this.widgetPresenter.fetchMetadata());
            let value = NamaCommon.getFieldValue(this.widgetPresenter.fetchRecord(), fieldId);
            questionFields.push({
              id: fieldId,
              objValue: value,
              value: FieldTypesUtils.toString(fieldMetadata.fieldType, value)
            });
          }
        }
      }
    }
    let overriddenQuestions = this.block.overriddenQuestions;
    if (!overriddenQuestions || !overriddenQuestions.length)
      return questionFields;
    let overriddenQuestionsIds = overriddenQuestions.map(l => l.id);
    questionFields = questionFields.filter(l => !overriddenQuestionsIds.contains(l.id));
    questionFields.push(...(overriddenQuestions ?? []));
    return questionFields;
  }

  findLinkedFields(linkFieldId: string | undefined, questionFields: DTOFilledQuestionField[]) {
    let returnedFields: DTOFilledQuestionField[] = [];
    if (!linkFieldId)
      return [];
    if (linkFieldId.includes(",")) {
      let parts = linkFieldId.split(",");
      for (let qId of parts)
        returnedFields.push(...this.findLinkedFields(qId, questionFields));
      return returnedFields;
    }
    if (!questionFields.length)
      return returnedFields;
    let idWithDot = linkFieldId + ".";
    for (let field of questionFields) {
      let questionFieldWithDot = field.id + ".";
      if (questionFieldWithDot.startsWith(idWithDot))
        returnedFields.push(field);
    }
    return returnedFields;
  }

  createNewFieldQuestionIds(sourceId: string | undefined, source: namaTypes.QuestionField[], linkFields: namaTypes.DTOFilledQuestionField[]) {
    let newFieldIds: DTOFilledQuestionField[] = [];
    if (!sourceId)
      return newFieldIds;
    if (sourceId?.includes(",")) {
      let parts = sourceId.split(",");
      for (let part of parts)
        newFieldIds.push(...this.createNewFieldQuestionIds(part, source, linkFields));
      return newFieldIds;
    }
    if (!source.length)
      return newFieldIds;
    let idWithDot = sourceId + ".";
    for (let field of source) {
      let matched = linkFields.find(l => l.id === field.id);
      if (matched)
        continue;
      let questionFieldWithDot = field.id + ".";
      if (questionFieldWithDot.startsWith(idWithDot))
        newFieldIds.push({
          id: field.id
        });
    }
    return newFieldIds;
  }

  private readSourceFields(questionFields: namaTypes.DTOFilledQuestionField[], sourceField: string, linkFieldId: string | undefined) {
    if (sourceField.includes(",")) {
      let sourceFieldParts = sourceField.split(",");
      let linkFieldParts = (linkFieldId ?? "").split(",");
      for (let i = 0; i < sourceFieldParts.length; i++)
        this.readSourceFields(questionFields, sourceFieldParts[i], linkFieldParts[i]);
      return;
    }
    let fieldValue = NamaCommon.getFieldValue(this.widgetPresenter.fetchRecord(), sourceField);
    if (!fieldValue)
      return;
    let fieldMetadata = MetaData.findFieldMetadata(sourceField, this.widgetPresenter.fetchMetadata());
    questionFields.push({
      id: linkFieldId,
      value: FieldTypesUtils.toString(fieldMetadata.fieldType, fieldValue),
      objValue: fieldValue
    });
  }

  override shouldUpdateWindowTitle(): boolean {
    return false;
  }
}
